import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useColorModeValue,
  Icon,
  Flex,
  Spacer,
  useToast,
  Divider
} from '@chakra-ui/react';
import {
  FiUsers,
  FiBook,
  FiTrendingUp,
  FiMessageSquare,
  FiAlertCircle,
  FiPlus,
  FiSettings,
  FiBarChart,
  FiHome,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiFileText
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import Layout from '../shared/Layout';
import Sidebar2 from '../shared/Sidebar2';
import LayoutDashboard from '../shared/LayoutDashboard';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/dashboard');
      setDashboardData(response.data.data);
    } catch (error) {
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'حدث خطأ أثناء تحميل بيانات لوحة التحكم',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, change, icon, color = 'blue' }) => (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" textAlign="right" dir="rtl">
      <CardBody>
        <Flex>
          <Box>
            <Stat>
              <StatLabel fontSize="sm" color="gray.500">{title}</StatLabel>
              <StatNumber fontSize="2xl" fontWeight="bold">{value}</StatNumber>
              {change && (
                <StatHelpText>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              )}
            </Stat>
          </Box>
          <Spacer />
          <Box>
            <Icon as={icon} boxSize={8} color={`${color}.500`} />
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon, onClick, color = 'blue' }) => (
    <Card 
      bg={cardBg} 
      borderColor={borderColor} 
      borderWidth="1px"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
      transition="all 0.2s"
      textAlign="right"
      dir="rtl"
    >
      <CardBody>
        <VStack spacing={3}>
          <Icon as={icon} boxSize={8} color={`${color}.500`} />
          <Text fontWeight="bold" textAlign="center">{title}</Text>
          <Text fontSize="sm" color="gray.500" textAlign="center">{description}</Text>
        </VStack>
      </CardBody>
    </Card>
  );

  

  if (loading) {
    return (
      <Layout>
        <Container maxW="7xl" py={8}>
          <Text>جاري التحميل...</Text>
        </Container>
      </Layout>
    );
  }

  return (
    <LayoutDashboard>
      <Sidebar2 />
      <Container maxW={{ base: '7xl', lg: 'calc(7xl - 250px)' }} py={8} mr={{ lg: '250px' }}>
        <VStack spacing={8} align="stretch" dir="rtl">
          {/* Header */}
          <Box textAlign="right">
            <Heading size="lg" mb={2}>لوحة تحكم المدير</Heading>
            <Text color="gray.500">مرحباً بك في لوحة تحكم المنصة</Text>
          </Box>

          {/* Statistics Cards */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={6}>
            <StatCard
              title="إجمالي المستخدمين"
              value={dashboardData?.users?.total || 0}
              change={dashboardData?.users?.growthRate}
              icon={FiUsers}
              color="blue"
            />
            <StatCard
              title="الطلاب"
              value={dashboardData?.users?.students || 0}
              icon={FiUsers}
              color="green"
            />
            <StatCard
              title="المدرسين"
              value={dashboardData?.users?.instructors || 0}
              icon={FiUsers}
              color="purple"
            />
            <StatCard
              title="الدورات"
              value={dashboardData?.courses?.total || 0}
              change={dashboardData?.courses?.enrollmentGrowthRate}
              icon={FiBook}
              color="orange"
            />
          </Grid>

     

          {/* Support Statistics */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <StatCard
              title="الشكاوى المعلقة"
              value={dashboardData?.support?.pendingReclamations || 0}
              icon={FiAlertCircle}
              color="red"
            />
            <StatCard
              title="التقييمات المعلقة"
              value={dashboardData?.support?.pendingFeedback || 0}
              icon={FiMessageSquare}
              color="yellow"
            />
          </Grid>

          {/* Quick Actions */}
          <Box textAlign="right">
            <Heading size="md" mb={4}>الإجراءات السريعة</Heading>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
              <QuickActionCard
                title="إضافة مستخدم"
                description="إنشاء حساب جديد للطلاب أو المدرسين"
                icon={FiPlus}
                onClick={() => navigate('/admin/users/create')}
                color="green"
              />
              <QuickActionCard
                title="إنشاء دورة"
                description="إضافة دورة جديدة وتعيين مدرس"
                icon={FiBook}
                onClick={() => navigate('/admin/courses/create')}
                color="blue"
              />
    
            </Grid>
          </Box>

          {/* Recent Activity */}
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
            {/* Recent Users */}
            <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" textAlign="right" dir="rtl">
              <CardHeader>
                <Heading size="md">المستخدمون الجدد</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={3} align="stretch">
                  {dashboardData?.recent?.users?.map((user, index) => (
                    <Flex key={index} align="center">
                      <Box>
                        <Text fontWeight="bold">{user.username}</Text>
                        <Text fontSize="sm" color="gray.500">{user.email}</Text>
                      </Box>
                      <Spacer />
                      <Badge colorScheme={
                        user.role === 'admin' ? 'red' : 
                        user.role === 'instructor' ? 'purple' : 'blue'
                      }>
                        {user.role === 'admin' ? 'مدير' : 
                         user.role === 'instructor' ? 'مدرس' : 'طالب'}
                      </Badge>
                    </Flex>
                  )) || <Text color="gray.500">لا توجد بيانات</Text>}
                </VStack>
              </CardBody>
            </Card>

          </Grid>
        </VStack>
      </Container>
    </LayoutDashboard>
  );
};

export default AdminDashboard;